import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Mail, Phone, User, FileText, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewAppointments = () => {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/admin/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await axios.delete(`${API}/admin/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment deleted successfully');
      fetchAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center space-x-4">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Appointment Requests</h1>
            <p className="text-sm text-gray-600">{appointments.length} total requests</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {appointments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Appointments Yet</h3>
              <p className="text-gray-600">Appointment requests from clients will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {appointments.map((appointment) => (
              <Card key={appointment.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{appointment.name}</h3>
                          <p className="text-sm text-gray-500">{formatDate(appointment.created_at)}</p>
                        </div>
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                          {appointment.status}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3 text-gray-700">
                          <Mail className="h-5 w-5 text-teal-600" />
                          <span>{appointment.email}</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-700">
                          <Phone className="h-5 w-5 text-teal-600" />
                          <span>{appointment.phone}</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-700">
                          <User className="h-5 w-5 text-teal-600" />
                          <span>Preferred: {appointment.preferredPartner}</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-700">
                          <FileText className="h-5 w-5 text-teal-600" />
                          <span>{appointment.taxType}</span>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Filing Status:</p>
                        <p className="text-gray-900">{appointment.filingStatus}</p>
                      </div>

                      {appointment.message && (
                        <div className="bg-teal-50 p-4 rounded-lg">
                          <p className="text-sm font-semibold text-teal-900 mb-1">Additional Information:</p>
                          <p className="text-teal-800">{appointment.message}</p>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => handleDelete(appointment.id)}
                      variant="outline"
                      size="icon"
                      className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ViewAppointments;
